import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Image, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	DailyBonusBgGif,
	DailyBonusEnGif,
	DailyBonusFrGif,
	DailyBonusRuGif,
	Num0Png,
	Num1Png,
	Num2Png,
	Num3Png,
	Num4Png,
	Num5Png,
	Num6Png,
	Num7Png,
	Num8Png,
	Num9Png,
} from '../../../../assets';
import { RootState } from '../../../../store';
import { hideCollectBonusAnimation } from '../../../../store/bonus/bonusActions';
import { useDevice } from '../../../../utils/hooks/useDevice';
import LS from '../../../../utils/localStorageUtils';

const NumberPngs = {
	'0': Num0Png,
	'1': Num1Png,
	'2': Num2Png,
	'3': Num3Png,
	'4': Num4Png,
	'5': Num5Png,
	'6': Num6Png,
	'7': Num7Png,
	'8': Num8Png,
	'9': Num9Png,
} as const;

function pickDailyBonusImage(langRaw: unknown) {
	const lang = String(langRaw || 'en').toLowerCase();
	switch (lang) {
		case 'bg':
			return DailyBonusBgGif;
		case 'fr':
			return DailyBonusFrGif;
		case 'ru':
			return DailyBonusRuGif;
		case 'en':
		default:
			return DailyBonusEnGif;
	}
}

// ─── Web version ────────────────────────────────────────────────────────────

function DailyBonusOverlayWeb({
	show,
	chips,
	bgSource,
	onHide,
}: {
	show: boolean;
	chips: number;
	bgSource: ReturnType<typeof pickDailyBonusImage>;
	onHide: () => void;
}) {
	const [opacity, setOpacity] = useState(0);
	const { width } = useWindowDimensions();
	const isMobileWeb = width <= 768;

	useEffect(() => {
		if (!show) return;

		setOpacity(0);

		// Allow the 0-opacity frame to commit before starting the fade-in
		const fadeIn = requestAnimationFrame(() => setOpacity(1));
		let fadeOutTimer: ReturnType<typeof setTimeout> | null = null;

		const hideTimer = setTimeout(() => {
			setOpacity(0);
			fadeOutTimer = setTimeout(onHide, 420);
		}, 4200);

		return () => {
			cancelAnimationFrame(fadeIn);
			clearTimeout(hideTimer);
			if (fadeOutTimer !== null) clearTimeout(fadeOutTimer);
		};
	}, [show, onHide]);

	if (!show) return null;

	const contentStyle = {
		opacity,
		// React Native Web forwards unknown style props to the DOM element
		transition: 'opacity 0.22s ease',
	} as any;

	return (
		<View style={styles.root} pointerEvents='none'>
			<Image
				source={bgSource}
				resizeMode='contain'
				style={[styles.image, isMobileWeb && webMobileStyles.image, contentStyle]}
			/>
			<View style={[styles.amountWrap, isMobileWeb && webMobileStyles.amountWrap, contentStyle]}>
				<View style={[styles.digitsRow, isMobileWeb && webMobileStyles.digitsRow]}>
					{String(chips)
						.split('')
						.map((d, idx) => (
							<Image
								key={`${d}:${idx}`}
								source={NumberPngs[d as keyof typeof NumberPngs]}
								resizeMode='contain'
								style={[styles.digit, isMobileWeb && webMobileStyles.digit]}
							/>
						))}
				</View>
			</View>
		</View>
	);
}

// ─── Native version ──────────────────────────────────────────────────────────

function DailyBonusOverlayNative({
	show,
	chips,
	bgSource,
	onHide,
}: {
	show: boolean;
	chips: number;
	bgSource: ReturnType<typeof pickDailyBonusImage>;
	onHide: () => void;
}) {
	const opacity = useRef(new Animated.Value(0)).current;

	useEffect(() => {
		if (!show) return;

		opacity.setValue(0);

		const inAnim = Animated.timing(opacity, { toValue: 1, duration: 220, useNativeDriver: true });

		const outAnim = Animated.timing(opacity, { toValue: 0, duration: 420, useNativeDriver: true });

		inAnim.start();
		const t = setTimeout(() => outAnim.start(onHide), 4200);

		return () => clearTimeout(t);
	}, [show, onHide, opacity]);

	if (!show) return null;

	return (
		<Animated.View style={[styles.root, { opacity }]} pointerEvents='none'>
			<Image source={bgSource} resizeMode='contain' style={[styles.image, nativeStyles.image]} />
			<View style={[styles.amountWrap, nativeStyles.amountWrap]}>
				<View style={nativeStyles.amountScaleWrap}>
					<View style={[styles.digitsRow, nativeStyles.digitsRow]}>
						{String(chips)
							.split('')
							.map((d, idx) => (
								<Image
									key={`${d}:${idx}`}
									source={NumberPngs[d as keyof typeof NumberPngs]}
									resizeMode='contain'
									style={nativeStyles.digit}
								/>
							))}
					</View>
				</View>
			</View>
		</Animated.View>
	);
}

// ─── Shell ───────────────────────────────────────────────────────────────────

export default function DailyBonusOverlayAnimation() {
	const dispatch = useDispatch();
	const { isWeb } = useDevice();
	const show = useSelector((s: RootState) => s.bonus.showCollectAnimation);
	const chips = useSelector((s: RootState) => s.bonus.lastCollectedChips ?? 0);
	const bgSource = useMemo(() => pickDailyBonusImage(LS.get('lang')), []);
	const onHide = useCallback(() => dispatch(hideCollectBonusAnimation()), [dispatch]);

	if (isWeb) {
		return <DailyBonusOverlayWeb show={show} chips={chips} bgSource={bgSource} onHide={onHide} />;
	}
	return <DailyBonusOverlayNative show={show} chips={chips} bgSource={bgSource} onHide={onHide} />;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
	root: {
		...StyleSheet.absoluteFill,
		zIndex: 999999,
		elevation: 999999,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(0,0,0,0.35)',
	},
	image: {
		width: '92%',
		maxWidth: 720,
		aspectRatio: 900 / 506,
	},
	amountWrap: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		width: '80%',
		maxWidth: 520,
	},
	digitsRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 10,
	},
	digit: {
		height: 102,
		aspectRatio: 0.66,
	},
});

// Smaller overrides for native (Android / iOS)
const nativeStyles = StyleSheet.create({
	image: {
		width: 320,
		maxWidth: 320,
		aspectRatio: 900 / 506,
	},
	amountWrap: {
		width: 340,
		maxWidth: 340,
	},
	amountScaleWrap: {
		transform: [{ scale: 1.15 }],
	},
	digitsRow: {
		gap: 4,
	},
	digit: {
		width: 30,
		height: 48,
	},
});

const webMobileStyles = StyleSheet.create({
	image: {
		maxWidth: 360,
	},
	amountWrap: {
		maxWidth: 240,
	},
	digitsRow: {
		gap: 3,
	},
	digit: {
		height: 50,
	},
});
